// Mock transport for the imports tRPC client (PE-1886, Milestone A exit gate).
//
// WHY THIS EXISTS. The gate opens five parallel tracks; two of them (D wizard,
// E1 Import Hub) are frontend and would otherwise be blocked on Milestone B
// deploying job-lifecycle procedures to dev. These fixtures let D and E1 build
// and be reviewed against the REAL contract shapes with no backend at all. The
// parallelization map is explicit that E1 "needs only the mockup and the
// contract stubs".
//
// THE FIXTURES ARE TYPED AS `ImportJob`, DELIBERATELY. They are not loose
// objects — tsc rejects a fixture that drifts from the §3.1 contract. That is
// the point: if the contract changes, these break loudly rather than letting
// E1 build against a stale shape.
//
// COVERAGE RULE: one job per status, all thirteen. SPEC §6.1 requires that
// "Every one of the 13 states renders a badge and an action set — no state may
// fall through to a blank cell." A fixture set missing a state lets E1 ship
// with an untested blank cell, so `MOCK_IMPORT_JOBS` below is maintained with
// exactly one entry per `IMPORT_JOB_STATUSES` member. tsc cannot check that, so
// `imports.mock.test.ts` (co-located, per this package's convention) asserts it
// mechanically — all thirteen present, every fixture parsing against the real
// §3.1 schema, §3.1's counts invariant holding exactly where
// `countsShouldReconcile` says it applies, an attestation on every post-commit
// job (§3.2/§4.4), §3.5's deleted+retained partition of the created set, and
// newest-first ordering on distinct timestamps.

import {
  IMPORT_JOB_STATUSES,
  cappedProposalTier,
  countsReconcile,
  countsShouldReconcile,
  proposeMappingInputSchema,
  type ColumnProposal,
  type ColumnTarget,
  type ImportJob,
  type ImportJobStatus,
  type JobsGetInput,
  type JobsGetOutput,
  type JobsListInput,
  type JobsListOutput,
  type MappingProposal,
  type MappingProvenance,
  type ProposeMappingInput,
  type ProposeMappingOutput,
} from '@charitystack/imports-contracts';

const ORG_ID = 'org_mock_import_fixtures';
const ACTOR_ID = 'user_mock_akif';
const ACTOR_NAME = 'Akif Rahman';

/**
 * Fixed timestamps — never Date.now(), so fixtures are deterministic.
 *
 * EVERY FIXTURE GETS ITS OWN `createdAt`, and `MOCK_IMPORT_JOBS` is ordered by
 * it descending. §3.1's GSI is `orgId` + `createdAt` and §6.1 says the Hub
 * shows "one row per import job, newest first", so a fixture set with thirteen
 * identical timestamps would let E1's list render in whatever order the array
 * happens to be in and still look correct — the exact bug that surfaces the
 * day the real GSI query lands. Distinct, descending timestamps make the
 * mock's order the same order the backend will produce.
 *
 * The spread also reads like a real Hub: drafts are the newest rows (someone
 * is mid-wizard right now), the reversed import is the oldest (it succeeded
 * days ago and was reversed afterwards).
 *
 * Per-fixture derived stamps (`lastProgressAt`, `completedAt`, `reversal.*`)
 * are all AFTER that fixture's own `createdAt`, so E1 can compute a duration
 * from any fixture without getting a negative number.
 */
const T = {
  /** Pre-commit drafts — the newest rows in the Hub. */
  stagedCreated: '2026-08-07T16:41:09.000Z',
  mappingCreated: '2026-08-07T16:12:33.000Z',
  validatingCreated: '2026-08-07T15:58:02.000Z',
  readyCreated: '2026-08-07T15:20:47.000Z',

  /** Working states — started earlier today, still running. */
  committingCreated: '2026-08-07T14:30:00.000Z',
  committingProgress: '2026-08-07T14:36:12.000Z',
  cancellingCreated: '2026-08-07T13:05:00.000Z',
  cancellingProgress: '2026-08-07T13:11:48.000Z',

  /** Terminal states — yesterday. */
  successCreated: '2026-08-06T11:02:11.000Z',
  successProgress: '2026-08-06T11:06:40.000Z',
  successCompleted: '2026-08-06T11:07:02.000Z',
  failedCreated: '2026-08-06T09:44:00.000Z',
  failedCompleted: '2026-08-06T09:48:19.000Z',
  needsCleanupCreated: '2026-08-05T17:22:05.000Z',
  needsCleanupCompleted: '2026-08-05T17:29:51.000Z',
  cleanedUpCreated: '2026-08-05T16:40:12.000Z',
  cleanedUpCompleted: '2026-08-05T16:48:33.000Z',
  cancelledCreated: '2026-08-05T15:10:30.000Z',
  cancelledCompleted: '2026-08-05T15:14:07.000Z',

  /** Reversal states — the oldest imports; both succeeded before reversing. */
  reversingCreated: '2026-08-04T14:02:11.000Z',
  reversingCompleted: '2026-08-04T14:07:02.000Z',
  reversingRequested: '2026-08-07T09:15:00.000Z',
  reversedCreated: '2026-08-03T10:18:44.000Z',
  reversedCompleted: '2026-08-03T10:24:16.000Z',
  reversalRequested: '2026-08-05T09:15:00.000Z',
  reversalCompleted: '2026-08-05T09:17:30.000Z',
} as const;

/** A §5.1-shaped confirmed mapping, as §3.1's `mappingConfig`. */
const MAPPING_CONFIG = {
  columns: {
    'First Name': 'firstName',
    'Last Name': 'lastName',
    'Email Address': 'email',
    'Phone Number': 'phone',
    'T-Shirt Size': null, // §4.2's "skip" option — retained, not dropped
  },
  defaultPhoneRegion: 'US',
  detectedSource: 'mailchimp',
} as const;

/** The row count of the fixture file. Every fixture's `counts.total` is this. */
const FILE_ROW_COUNT = 1240;

/**
 * Completion counts, satisfying §3.1's invariant
 * `total = created + merged + excluded + errored`.
 *
 * §3.1's invariant applies at the statuses `countsShouldReconcile` names
 * (SUCCESS and the two reversal states — reversal outcomes go to the separate
 * `reversal` map and do not rewrite `counts`). Mid-flight and rolled-back
 * fixtures deliberately do NOT reconcile, because real jobs in those states
 * don't either.
 */
const COUNTS_DONE = {
  total: FILE_ROW_COUNT,
  created: 1102,
  merged: 118,
  excluded: 15,
  errored: 5,
};

/** Mid-flight counts: total known from validation, rows still landing. */
const COUNTS_PARTIAL = {
  total: FILE_ROW_COUNT,
  created: 640,
  merged: 71,
  excluded: 9,
  errored: 2,
};

/**
 * Counts after a rollback SUCCEEDED (FAILED, CANCELLED). §3.2: "the rollback
 * **succeeded** — created contacts removed, merge deltas reverted", so nothing
 * remains written and the four outcome counts are 0 — while `total` still
 * records how many rows the file had. Deliberately does not reconcile;
 * `countsShouldReconcile` excludes both statuses.
 */
const COUNTS_ROLLED_BACK = {
  total: FILE_ROW_COUNT,
  created: 0,
  merged: 0,
  excluded: 0,
  errored: 0,
};

/**
 * READY counts. `total` is the FILE'S ROW COUNT (§3.1: `counts` is written
 * "once validation has run"; the Hub's §6.1 Counts column reads `total` as the
 * size of the import), so it is 1240 here exactly as everywhere else — a
 * READY fixture whose `total` was the excluded-row count would make the user's
 * 1240-row file vanish from the Hub.
 *
 * `errored` is 0 on purpose, and that is not a rounding choice: §3.3.1 defines
 * `errored` as a WRITE-time Category-A outcome incremented inside a batch step
 * ("The workflow increments `counts.errored`"). No batch step has run at
 * READY, so nothing can be errored yet. Pre-commit validation produces only
 * `excluded` rows (§4.4's summary: "K rows excluded (with reasons)").
 *
 * `created`/`merged` stay 0 because no write has happened. §4.4's summary
 * screen renders "will create / will merge" from the validation preview, not
 * from `counts`. So this deliberately does NOT satisfy §3.1's invariant
 * (0 + 0 + 20 + 0 ≠ 1240) — and it must not, since `countsShouldReconcile`
 * returns false for READY.
 */
const COUNTS_READY = {
  total: FILE_ROW_COUNT,
  created: 0,
  merged: 0,
  excluded: 20,
  errored: 0,
};

/**
 * `createdAt` is a REQUIRED positional argument, not an override with a shared
 * default. A default would silently give two fixtures the same GSI sort key
 * again the next time someone adds a status.
 */
/**
 * The §3.1 `consentAttestation` map, as §4.4 step 1 records it at COMMIT.
 *
 * EVERY POST-COMMIT FIXTURE CARRIES THIS. It is not decoration: §3.2's only
 * inbound edge to COMMITTING is "READY → COMMITTING (attestation recorded)",
 * and every one of COMMITTING / CANCELLING / SUCCESS / FAILED /
 * FAILED_NEEDS_CLEANUP / CANCELLED / REVERSING / REVERSED is reachable ONLY
 * through COMMITTING. A fixture in one of those states without an attestation
 * is state-machine-impossible, and it would let E1 ship a Hub that renders a
 * blank where the audit trail belongs.
 *
 * `timestamp` is the fixture's own COMMIT moment, so it is passed in rather
 * than shared — §9.2 records "who attested or declined ... and when", and a
 * timestamp from another job's commit is not that.
 *
 * `attested: true` throughout; §3.1/§9.2 are explicit that `attested: false`
 * is also a valid recorded outcome (decline-to-attest still imports, §9.1), so
 * the CANCELLED fixture below carries the false case to keep E1 honest.
 */
function attestation(timestamp: string, attested = true) {
  return { attested, actorId: ACTOR_ID, timestamp, copyVersion: 'v1' };
}

function baseJob(
  status: ImportJobStatus,
  createdAt: string,
  overrides: Partial<ImportJob> = {},
): ImportJob {
  return {
    jobId: `job_mock_${status.toLowerCase()}`,
    // SPEC §3.1's sort key — always the literal "JOB" for a job record.
    recordType: 'JOB',
    orgId: ORG_ID,
    createdAt,
    actorId: ACTOR_ID,
    actorName: ACTOR_NAME,
    type: 'contacts',
    status,
    fileName: 'mailchimp_subscribed_export.csv',
    sourceFileKey: `imports/${ORG_ID}/job_mock_${status.toLowerCase()}/source.csv`,
    ...overrides,
  };
}

/**
 * One job per §3.2 status. Each carries exactly the fields that status would
 * really have — a STAGED job has no mappingConfig, a SUCCESS job has counts +
 * resultsFileKey + completedAt, a FAILED job has a failureReason, and every
 * post-COMMIT job has a consentAttestation. Fixtures that over-populate would
 * let E1 render fields that are absent in production; fixtures that
 * under-populate let E1 render a blank where production has data.
 *
 * ORDER IS NEWEST-FIRST BY `createdAt`, not §3.2 lifecycle order. §6.1's Hub is
 * "one row per import job, newest first (served by the jobs GSI — `orgId` +
 * `createdAt`, §3.1)", and `listMockImportJobs` below pages this array
 * positionally, so array order IS the mock's sort order. Lifecycle order would
 * hand E1 roughly the reverse of what the backend returns. The §3.2 grouping
 * survives in the comments.
 */
export const MOCK_IMPORT_JOBS: readonly ImportJob[] = [
  // --- newest: pre-commit drafts (§6.1 renders all four as "Draft") --------
  // No consentAttestation on any of these four: §4.4 step 1 records it at
  // COMMIT, so a draft that had one would be impossible.
  baseJob('STAGED', T.stagedCreated),
  baseJob('MAPPING', T.mappingCreated, { mappingConfig: MAPPING_CONFIG }),
  baseJob('VALIDATING', T.validatingCreated, { mappingConfig: MAPPING_CONFIG }),
  // At READY, validation has run — `total` is known and exclusions are known —
  // but no write has happened, so created/merged/errored are all 0. See
  // COUNTS_READY for why `errored` in particular must be 0 here.
  baseJob('READY', T.readyCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_READY,
  }),

  // --- writing (§6.1: "In progress" / "Cancelling…") ----------------------
  baseJob('COMMITTING', T.committingCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_PARTIAL,
    consentAttestation: attestation(T.committingCreated),
    lastProgressAt: T.committingProgress,
  }),
  // CANCELLING is only reachable from COMMITTING (§3.2), so it carries the
  // attestation that got the job there — plus the flag that started the
  // cancel.
  baseJob('CANCELLING', T.cancellingCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_PARTIAL,
    consentAttestation: attestation(T.cancellingCreated),
    cancelRequested: true,
    lastProgressAt: T.cancellingProgress,
  }),

  // --- terminal ------------------------------------------------------------
  baseJob('SUCCESS', T.successCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_DONE,
    consentAttestation: attestation(T.successCreated),
    lastProgressAt: T.successProgress,
    resultsFileKey: `imports/${ORG_ID}/job_mock_success/results.csv`,
    completedAt: T.successCompleted,
  }),
  baseJob('FAILED', T.failedCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_ROLLED_BACK,
    consentAttestation: attestation(T.failedCreated),
    failureReason: 'The import could not be completed. All changes were rolled back.',
    resultsFileKey: `imports/${ORG_ID}/job_mock_failed/results.csv`,
    completedAt: T.failedCompleted,
  }),
  baseJob('FAILED_NEEDS_CLEANUP', T.needsCleanupCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_PARTIAL,
    consentAttestation: attestation(T.needsCleanupCreated),
    failureReason:
      'The import failed and automatic cleanup did not finish. Contact support.',
    completedAt: T.needsCleanupCompleted,
  }),
  // §3.2: CLEANED_UP is the operator-only exit from FAILED_NEEDS_CLEANUP —
  // support confirmed the manual cleanup is done. Counts stay whatever the
  // failed run reached (partial data was reconciled by hand, not re-counted);
  // §6.1 renders "Download results (if any)", so this fixture carries a
  // results file to exercise the present-file branch (the
  // FAILED_NEEDS_CLEANUP fixture above exercises the absent one).
  baseJob('CLEANED_UP', T.cleanedUpCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_PARTIAL,
    // Reachable only via COMMITTING → FAILED_NEEDS_CLEANUP, so it carries an
    // attestation like every other post-commit state (§3.2/§4.4).
    consentAttestation: attestation(T.cleanedUpCreated),
    failureReason:
      'The import failed and automatic cleanup did not finish. Our team completed the cleanup.',
    resultsFileKey: `imports/${ORG_ID}/job_mock_cleaned_up/results.csv`,
    completedAt: T.cleanedUpCompleted,
  }),
  // §3.2: CANCELLED has two honest flavors. This is the mid-commit one
  // (COMMITTING → CANCELLING → CANCELLED, rolled back), so it has an
  // attestation; the pre-commit flavor would carry neither counts nor one.
  // This fixture also carries the `attested: false` case that §3.1/§9.1 call a
  // valid recorded outcome — declining still imports, and E1 must render it as
  // a real "declined", never as "missing".
  baseJob('CANCELLED', T.cancelledCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_ROLLED_BACK,
    consentAttestation: attestation(T.cancelledCreated, false),
    cancelRequested: true,
    completedAt: T.cancelledCompleted,
  }),

  // --- oldest: reversal (both descend from SUCCESS, §3.2) ------------------
  // REVERSING carries ONLY requestedAt + actorId. §3.5's finalize step is what
  // writes deleted/retained/completedAt, so while the reversal is still running
  // all three are genuinely ABSENT — not zero. A fixture with deleted: 0 would
  // teach E1 to render "0 deleted" for a reversal that has in fact deleted
  // plenty and simply hasn't reported yet. E1 must key "is the reversal
  // finished?" off `reversal.completedAt`, not off the counts.
  baseJob('REVERSING', T.reversingCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_DONE,
    consentAttestation: attestation(T.reversingCreated),
    lastProgressAt: T.reversingRequested,
    completedAt: T.reversingCompleted,
    resultsFileKey: `imports/${ORG_ID}/job_mock_reversing/results.csv`,
    reversal: {
      requestedAt: T.reversingRequested,
      actorId: ACTOR_ID,
    },
  }),
  baseJob('REVERSED', T.reversedCreated, {
    mappingConfig: MAPPING_CONFIG,
    counts: COUNTS_DONE,
    consentAttestation: attestation(T.reversedCreated),
    completedAt: T.reversedCompleted,
    resultsFileKey: `imports/${ORG_ID}/job_mock_reversed/results.csv`,
    reversal: {
      requestedAt: T.reversalRequested,
      actorId: ACTOR_ID,
      // §3.5 step 1: created contacts are "deleted ... **unless the contact has
      // acquired donation activity since import** ... Those contacts are
      // **retained**, counted in `reversal.retained`". So deleted and retained
      // partition the contacts this import CREATED, and must sum to
      // counts.created (1102) — not to created + merged. §3.5 step 2 handles
      // merged contacts on a separate track (merge deltas reverted per-field,
      // with the org's later edits kept), reported as "kept fields", never as
      // deleted or retained contacts.
      //
      // §6.3/§3.5: retained ≠ 0 is the normal case — donors who gave since the
      // import are deliberately kept. E1's reversal summary must render this.
      deleted: 1046,
      retained: 56,
      completedAt: T.reversalCompleted,
    },
  }),
];

/**
 * Mock `imports.jobs.list`. Pages `MOCK_IMPORT_JOBS` positionally, and that
 * array is maintained in `createdAt`-descending order, so the ordering this
 * returns is the same newest-first ordering the real §3.1 GSI query (`orgId` +
 * `createdAt`) produces — E1's list code does not change when the backend
 * lands. Keep the array sorted; this function does not sort for you.
 *
 * The cursor is the index of the next item, encoded as a string — an opaque
 * token to callers, exactly as the real one will be.
 */
export async function listMockImportJobs(
  input: JobsListInput = { limit: 30 },
): Promise<JobsListOutput> {
  const limit = input.limit ?? 30;
  const start = input.cursor ? Number.parseInt(input.cursor, 10) : 0;
  if (Number.isNaN(start) || start < 0) {
    throw new Error(`listMockImportJobs: invalid cursor "${input.cursor}"`);
  }

  const page = MOCK_IMPORT_JOBS.slice(start, start + limit);
  const next = start + limit;

  return {
    jobs: page,
    nextCursor: next < MOCK_IMPORT_JOBS.length ? String(next) : undefined,
  };
}

/**
 * Mock `imports.jobs.get` — §6.2's per-job poll against the same fixtures
 * `listMockImportJobs` pages, so the Hub's poller behaves offline exactly as it
 * does live (the row it follows is the row the list showed).
 *
 * Throws on an unknown id rather than returning a null-ish job: the real
 * procedure is tenant-scoped and NOT_FOUNDs a job the caller cannot see, and a
 * mock that answered "no job" softly would let a caller ship a missing
 * not-found path that only fails against the live backend.
 */
export async function getMockImportJob(
  input: JobsGetInput,
): Promise<JobsGetOutput> {
  const job = MOCK_IMPORT_JOBS.find((candidate) => candidate.jobId === input.jobId);
  if (!job) {
    throw new Error(`getMockImportJob: no fixture job with id "${input.jobId}"`);
  }
  return { job };
}

/**
 * Re-exported so `imports.mock.test.ts` can assert coverage and reconciliation
 * without duplicating the status list or re-deriving the invariant.
 */
export { IMPORT_JOB_STATUSES, countsReconcile, countsShouldReconcile };

// ===========================================================================
// PE-1906 — the mapping proposal fixture
// ===========================================================================
//
// A REAL `MappingProposal`, not a stub. Two properties make it worth having:
//
//   1. It is TOTAL over the headers it is given (P2 obligation 1). Every header
//      gets a key; a header it cannot judge gets `{target: null, confidence: 0}`
//      rather than being omitted. `proposalCoversAllHeaders()` checks totality
//      in both directions, so an omission or an invented key fails loudly.
//   2. Every tier is derived by the CONTRACT's `cappedProposalTier()`, never
//      chosen here. That is what keeps the consent cap (§9.3) — emailConsent /
//      smsConsent never reach AUTO — true in the fixture as well as in prod.
//
// The matching below is a deliberately simple normalized-keyword lookup. It is
// NOT the real engine (PE-1896, an LLM behind the P2 port); it exists so the
// wizard renders realistic confidence zones against the synthetic corpus while
// the backend is mocked.

/** Normalized header → target, mirroring §4.0.1's normalization. */
function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Keyword table covering the synthetic corpus (Bloomerang, Donorbox incl.
 * Spanish, Mailchimp, Neon, Givebutter, Klaviyo, ActiveCampaign,
 * Constant Contact). `confidence` is what a real engine would plausibly report.
 *
 * ⚠️ Consent columns are entered with their REAL polarity ambiguity in mind:
 * they carry high keyword confidence but the contract caps them at REVIEW, so
 * the human always confirms. `Do Not Email` is a documented inversion trap
 * (FINDINGS §4.3) — it maps to emailConsent, and the confirm step is what keeps
 * the inversion from shipping silently.
 */
const HEADER_KEYWORDS: Array<{
  match: RegExp;
  target: ColumnTarget;
  confidence: number;
  rationale: string;
}> = [
  { match: /^(email|email address|correo electronico|primary email|email 1)$/, target: 'email', confidence: 0.97, rationale: 'Exact email header match' },
  { match: /^(first name|firstname|given name|nombre)$/, target: 'firstName', confidence: 0.96, rationale: 'Exact first-name header match' },
  { match: /^(last name|lastname|surname|family name|apellido)$/, target: 'lastName', confidence: 0.96, rationale: 'Exact last-name header match' },
  { match: /^(full name|name|display name|contact name)$/, target: 'fullName', confidence: 0.88, rationale: 'Single combined name column' },
  { match: /^(phone|phone number|mobile|mobile number|cell|telefono|primary phone)$/, target: 'phone', confidence: 0.94, rationale: 'Phone header match' },
  { match: /^(address|address line 1|address 1|street address|street)$/, target: 'addressLine1', confidence: 0.92, rationale: 'Primary street address' },
  { match: /^(address line 2|address 2|apt|suite|unit)$/, target: 'addressLine2', confidence: 0.9, rationale: 'Secondary address line' },
  { match: /^(city|town|ciudad)$/, target: 'city', confidence: 0.95, rationale: 'City header match' },
  { match: /^(state|province|region|estado)$/, target: 'state', confidence: 0.93, rationale: 'State/province header match' },
  { match: /^(zip|zip code|postal code|postcode|codigo postal)$/, target: 'postalCode', confidence: 0.94, rationale: 'Postal code header match' },
  { match: /^(country|pais)$/, target: 'country', confidence: 0.94, rationale: 'Country header match' },
  { match: /^(id|contact id|constituent id|account id|external id|member id)$/, target: 'externalId', confidence: 0.85, rationale: 'Source-system identifier' },
  // Consent — capped at REVIEW by the contract regardless of these numbers.
  { match: /^(email consent|email opt in|subscribed|email subscription status|suscrito a actualizaciones|do not email|opt out|unsubscribed)$/, target: 'emailConsent', confidence: 0.91, rationale: 'Email consent signal — polarity needs confirmation' },
  { match: /^(sms consent|sms opt in|text opt in|do not text|sms subscription status)$/, target: 'smsConsent', confidence: 0.9, rationale: 'SMS consent signal — polarity needs confirmation' },
  // Sentinels.
  { match: /^(tags|tag|labels|groups)$/, target: 'TAG', confidence: 0.8, rationale: 'Free-form labels route to tags' },
  { match: /^(notes|note|comments|source|created at|date added|last modified)$/, target: 'CUSTOM_FIELD', confidence: 0.7, rationale: 'No catalog field — route to a custom field' },
];

function proposeForHeader(header: string): ColumnProposal {
  const normalized = normalizeHeader(header);
  const hit = HEADER_KEYWORDS.find((entry) => entry.match.test(normalized));

  if (!hit) {
    // P2 obligation 1: never omit. "Could not judge" is an explicit value.
    return { target: null, confidence: 0, tier: 'MANUAL' };
  }

  return {
    target: hit.target,
    confidence: hit.confidence,
    // Derived by the contract — the consent cap lives here, not in this file.
    tier: cappedProposalTier(hit.target, hit.confidence),
    rationale: hit.rationale,
  };
}

/**
 * Build a total `MappingProposal` for the given headers.
 *
 * Exported for the wizard's mock branch and for tests.
 */
export function buildMockMappingProposal(headers: string[]): MappingProposal {
  const proposals: Record<string, ColumnProposal> = {};
  for (const header of headers) {
    proposals[header] = proposeForHeader(header);
  }

  return {
    proposals,
    detectedPlatform: null,
    fingerprintMatched: false,
    engine: {
      provider: 'mock-keyword',
      version: '0.1.0',
      llmCalled: false,
    },
    degraded: false,
  };
}

/**
 * Mock `imports.proposeMapping` — the FULL `proposeMappingOutputSchema`
 * wrapper the real procedure returns, not the bare proposal. This is the seam
 * `api/imports.ts` consumes; `buildMockMappingProposal` above stays exported
 * for proposal-level tests (totality, consent caps) but is no longer what the
 * wizard receives.
 *
 * The sibling fields are derived the way the real backend derives them
 * (backends/imports/src/domain/mapping/propose-mapping.ts), given what the
 * mock honestly is — a keyword engine with NO org template:
 *
 *   - `tier: 3` — §4.0.1's cold-start tier. The tier classifier resolves
 *     headers against the org template, and an empty template classifies every
 *     file Tier 3. (`2` is unrepresentable in V1; `0` would falsely claim a
 *     template identity-match and render the confirmation summary over
 *     keyword guesses.)
 *   - `resolvedHeaders: []` — nothing came from a template.
 *   - `unresolvedHeaders` — all of them: "the columns actually sent to the
 *     provider", which at Tier 3 is the whole file.
 *   - `manualMappingRequired` mirrors `proposal.degraded` (the keyword engine
 *     never degrades, so it is false on the success path) — EXCEPT on
 *     duplicate headers, where the real port cannot faithfully answer (two
 *     identical names collapse to one record key) and the backend signals the
 *     manual fallback IN-BAND as a success. The mock does the same, so the
 *     wizard's duplicate-header handling is exercised against the shape the
 *     backend actually emits — never a thrown error.
 */
export function buildMockProposeMappingOutput(
  input: ProposeMappingInput,
): ProposeMappingOutput {
  // Validate exactly as the live backend does: the real procedure runs
  // `.input(proposeMappingInputSchema)`, so a bad input (empty headers, >500
  // headers, a non-UUID jobId) is a thrown tRPC BAD_REQUEST there — never a
  // response. Mirroring that here keeps mocked and live behavior in the same
  // class: an input the backend would reject must not "work" in dev.
  const validated = proposeMappingInputSchema.safeParse(input);
  if (!validated.success) {
    throw new Error(
      `proposeMapping: invalid input (the live backend rejects this as a tRPC BAD_REQUEST): ${JSON.stringify(
        validated.error.issues,
      )}`,
    );
  }

  const headers = input.headers;

  const duplicates = Array.from(
    new Set(headers.filter((header, index) => headers.indexOf(header) !== index)),
  );
  if (duplicates.length > 0) {
    // Mirrors the backend's duplicate-header branch: the proposal is the BASE
    // (nothing judged — the provider is never consulted, because its record
    // keyed by header cannot represent the file), and the manual fallback is
    // signalled on the wrapper, not thrown.
    const proposals: Record<string, ColumnProposal> = {};
    for (const header of headers) {
      proposals[header] = { target: null, confidence: 0, tier: 'MANUAL' };
    }
    return {
      proposal: {
        proposals,
        detectedPlatform: null,
        fingerprintMatched: false,
        engine: { provider: 'mock-keyword', version: '0.1.0', llmCalled: false },
        degraded: false,
      },
      tier: 3,
      resolvedHeaders: [],
      unresolvedHeaders: [...headers],
      manualMappingRequired: true,
      manualMappingReason: 'DUPLICATE_HEADERS',
      duplicateHeaders: duplicates,
    };
  }

  const proposal = buildMockMappingProposal([...headers]);
  return {
    proposal,
    tier: 3,
    resolvedHeaders: [],
    unresolvedHeaders: [...headers],
    manualMappingRequired: proposal.degraded,
    provenance: buildMockMappingProvenance([...headers], proposal),
  };
}

/** Provenance for the same proposal, so the confidence UI has real data. */
export function buildMockMappingProvenance(
  headers: string[],
  proposal: MappingProposal,
): MappingProvenance {
  const byHeader: Record<string, number> = {};
  const tierCounts = { AUTO: 0, REVIEW: 0, MANUAL: 0 };

  for (const header of headers) {
    const columnProposal = proposal.proposals[header];
    const confidence = columnProposal?.confidence ?? 0;
    byHeader[header] = confidence;
    const tier = cappedProposalTier(columnProposal?.target ?? null, confidence);
    tierCounts[tier] += 1;
  }

  return {
    byHeader,
    tierCounts,
    engine: 'mock-keyword',
    model: null,
    resolvedProvider: null,
  };
}
